/**
 * Lightweight & robust HTML sanitizer (no external deps).
 * Strips scripts, forbidden tags, event handlers, and dangerous URLs from CMS/user HTML.
 *
 * Uses DOMParser in browser/DOM (and jsdom/Jest) environments, with a hardened regex fallback.
 */

const FORBIDDEN_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "LINK",
  "META",
  "BASE",
  "FORM",
  "FRAME",
  "FRAMESET",
  "APPLET",
  "NOSCRIPT",
  "NOEMBED",
  "TEMPLATE",
]);

const DANGEROUS_URI_ATTRIBUTES = new Set([
  "href",
  "src",
  "xlink:href",
  "action",
  "formaction",
  "data",
  "poster",
  "background",
]);

function isDangerousUrl(url) {
  if (!url || typeof url !== "string") return false;

  // Decode HTML numeric and hex entities (e.g. &#x09;, &#58;, etc.) and remove control characters / whitespace
  const cleanUrl = url
    .replace(/&#(\d+);?/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/[\x00-\x20\x7F-\x9F\s]+/g, "")
    .toLowerCase();

  // Check for dangerous schemes: javascript:, vbscript:, data:text/html, etc.
  if (
    cleanUrl.startsWith("javascript:") ||
    cleanUrl.startsWith("vbscript:") ||
    cleanUrl.startsWith("data:text/html") ||
    cleanUrl.startsWith("data:text/javascript") ||
    cleanUrl.startsWith("data:application/javascript") ||
    cleanUrl.startsWith("data:application/x-javascript") ||
    cleanUrl.startsWith("data:text/xml") ||
    cleanUrl.startsWith("data:application/xml") ||
    cleanUrl.startsWith("data:image/svg+xml")
  ) {
    return true;
  }
  return false;
}

function sanitizeWithDom(html) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const body = doc.body;
    if (!body) return "";

    // 1. Remove all forbidden elements and their descendants
    const forbiddenElements = body.querySelectorAll(
      "script, style, iframe, object, embed, link, meta, base, form, frame, frameset, applet, noscript, noembed, template"
    );
    forbiddenElements.forEach((el) => el.remove());

    // 2. Clean all remaining elements
    const allElements = body.querySelectorAll("*");
    allElements.forEach((el) => {
      // Check tag name
      if (FORBIDDEN_TAGS.has(el.tagName.toUpperCase())) {
        el.remove();
        return;
      }

      // Inspect and clean attributes
      const attrs = Array.from(el.attributes);
      for (const attr of attrs) {
        const name = attr.name.toLowerCase();
        const value = attr.value;

        // Strip any event handler (starts with 'on', e.g. onload, onclick, onerror, onfocus...)
        if (name.startsWith("on")) {
          el.removeAttribute(attr.name);
          continue;
        }

        // Strip srcdoc on iframes
        if (name === "srcdoc") {
          el.removeAttribute(attr.name);
          continue;
        }

        // Neutralize dangerous URLs on attributes like href, src, action, formaction, data, etc.
        if (DANGEROUS_URI_ATTRIBUTES.has(name)) {
          if (isDangerousUrl(value)) {
            if (name === "href") {
              el.setAttribute("href", "#");
            } else {
              el.removeAttribute(attr.name);
            }
          }
        }

        // Clean inline style attribute from CSS expressions or scripting
        if (name === "style" && value) {
          const lowerStyle = value.toLowerCase();
          if (
            lowerStyle.includes("expression(") ||
            lowerStyle.includes("javascript:") ||
            lowerStyle.includes("vbscript:") ||
            lowerStyle.includes("behavior:") ||
            lowerStyle.includes("-moz-binding")
          ) {
            el.removeAttribute("style");
          }
        }
      }
    });

    return body.innerHTML;
  } catch (_) {
    return sanitizeWithRegex(html);
  }
}

function sanitizeWithRegex(dirty) {
  let html = String(dirty);

  // Remove dangerous tags and their content
  html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  html = html.replace(/<script\b[^>]*>/gi, "");
  html = html.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "");
  html = html.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "");
  html = html.replace(/<iframe\b[^>]*>/gi, "");
  html = html.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, "");
  html = html.replace(/<embed\b[^>]*>/gi, "");
  html = html.replace(/<link\b[^>]*>/gi, "");
  html = html.replace(/<meta\b[^>]*>/gi, "");
  html = html.replace(/<base\b[^>]*>/gi, "");
  html = html.replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, "");
  html = html.replace(/<form\b[^>]*>/gi, "");

  // Strip inline event handlers (supports unquoted, single-quoted, double-quoted, and no-space before 'on')
  html = html.replace(/[\s/>]+on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, (match) => {
    return match.startsWith("/") ? "/" : match.startsWith(">") ? ">" : " ";
  });
  html = html.replace(/[\s/>]+on[a-zA-Z]+(?=[\s/>])/gi, " ");

  // Neutralize javascript: / vbscript: / data:text/html URLs (both quoted and unquoted)
  html = html.replace(/(href|src|xlink:href)\s*=\s*(['"])\s*(?:javascript|vbscript|data\s*:\s*text\/html)[\s\S]*?\2/gi, '$1="#"');
  html = html.replace(/(href|src|xlink:href)\s*=\s*(?:javascript|vbscript|data\s*:\s*text\/html)[^\s>]+/gi, '$1="#"');

  // Remove expression() CSS (legacy IE)
  html = html.replace(/expression\s*\(/gi, "invalid(");

  return html;
}

export function sanitizeHtml(dirty) {
  if (dirty == null || dirty === false) return "";
  const str = String(dirty);
  if (!str) return "";

  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    return sanitizeWithDom(str);
  }
  return sanitizeWithRegex(str);
}

export default sanitizeHtml;
